/** Thin fetch wrapper for the Taretto REST API (same origin, prefix /api). */

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

let recoverSession: (() => Promise<boolean>) | null = null;

/**
 * Hosted edition: on a 401 the app checks its session once (that request may refresh it, or pick up a cookie another
 * request just renewed) and retries; when the user really is signed out, the check shows the sign-in screen instead.
 */
export function setSessionRecovery(recover: (() => Promise<boolean>) | null): void {
  recoverSession = recover;
}

/** FormData is sent as multipart (the browser sets the boundary), a Blob as it is with its own type, anything else as JSON. */
async function request<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const form = body instanceof FormData;
  const blob = body instanceof Blob;
  const res = await fetch(`/api${path}`, {
    method,
    headers: body !== undefined && !form ? { 'Content-Type': blob ? body.type : 'application/json' } : undefined,
    body: body === undefined ? undefined : form || blob ? body : JSON.stringify(body),
  });
  if (res.status === 401 && !retried && path !== '/info' && recoverSession && (await recoverSession())) {
    return request<T>(method, path, body, true);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const err = data as { error?: string; details?: unknown } | undefined;
    throw new ApiRequestError(err?.error ?? `${method} ${path} failed (${res.status})`, res.status, err?.details);
  }
  return data as T;
}

/** Build a query string from defined, non-null params. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};
